import { useQuery, useQueryClient } from "@tanstack/react-query";

import { usePreferenceStorage } from "@/setup/frontend/storage/PreferenceStorageContext";

export interface LastKnownMFAStatus {
	isLoading: boolean;
	hasMFA: boolean | null;
	save: (hasMFA: boolean) => Promise<void>;
}

// MFA status can only be read with a fresh token. Remembering the last status
// read on this device lets MFAEnforcementModal keep blocking a user known to
// have no MFA after their token goes stale.
export function useLastKnownMFAStatus(
	userId: number | undefined,
): LastKnownMFAStatus {
	const preferenceStorage = usePreferenceStorage();
	const queryClient = useQueryClient();
	const queryKey = ["lastKnownMFAStatus", userId];

	const query = useQuery({
		queryKey,
		queryFn: async () => {
			const stored = await preferenceStorage
				.getItem(buildStorageKey(userId))
				.catch(() => null);
			return parseStoredStatus(stored);
		},
		enabled: userId !== undefined,
		staleTime: Number.POSITIVE_INFINITY,
		networkMode: "always",
	});

	const save = async (hasMFA: boolean) => {
		await preferenceStorage
			.setItem(buildStorageKey(userId), String(hasMFA))
			.catch(() => null);
		queryClient.setQueryData(queryKey, hasMFA);
	};

	return {
		isLoading: query.isPending,
		hasMFA: query.data ?? null,
		save,
	};
}

function buildStorageKey(userId: number | undefined): string {
	return `vcg-mfa-configured-${userId}`;
}

function parseStoredStatus(stored: string | null): boolean | null {
	if (stored === "true") {
		return true;
	}
	if (stored === "false") {
		return false;
	}
	return null;
}
